import { useClientPreferences } from '@/hooks/useClientPreferences';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';

const getAvatarUrl = (style: string, seed: string, bgColors?: string) =>
  `https://api.dicebear.com/9.x/${style}/svg?seed=${seed}${bgColors ? `&backgroundColor=${bgColors}` : ''}`;

function getPresetUrl(preset: string): string | null {
  if (!preset) return null;
  if (preset.includes(':')) {
    const [style, seed] = preset.split(':');
    return getAvatarUrl(style, seed);
  }
  return getAvatarUrl('avataaars', preset, 'b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf');
}

interface ClientAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /** If provided, show initials from this name instead of profile name */
  fallbackName?: string;
}

const sizeMap = {
  sm: 'w-8 h-8 text-xs rounded-lg',
  md: 'w-10 h-10 text-sm rounded-xl',
  lg: 'w-12 h-12 text-base rounded-2xl',
  xl: 'w-16 h-16 text-xl rounded-2xl',
};

/**
 * Renders the current client user's avatar based on their preferences.
 * Uses preset (DiceBear), custom (uploaded photo), or initials fallback.
 */
export function ClientAvatar({ size = 'md', className = '', fallbackName }: ClientAvatarProps) {
  const { preferences } = useClientPreferences();
  const { profile } = useCustomerAuth();

  const sizeClass = sizeMap[size];
  const name = fallbackName || profile?.full_name || profile?.email || 'U';
  const initial = name[0].toUpperCase();

  if (preferences.avatar_type === 'custom' && preferences.avatar_url) {
    return (
      <div className={`${sizeClass} overflow-hidden shrink-0 ${className}`}>
        <img src={preferences.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
      </div>
    );
  }

  if (preferences.avatar_type === 'preset' && preferences.avatar_preset) {
    const url = getPresetUrl(preferences.avatar_preset);
    if (url) {
      return (
        <div className={`${sizeClass} overflow-hidden shrink-0 ${className}`}>
          <img src={url} alt="Avatar" className="w-full h-full object-cover" />
        </div>
      );
    }
  }

  // Initials fallback
  return (
    <div className={`${sizeClass} bg-gradient-to-br from-accent/30 to-accent/10 flex items-center justify-center font-bold text-accent border border-accent/20 shrink-0 ${className}`}>
      {initial}
    </div>
  );
}
